import {
  CSSProperties,
  memo,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import Header from './Header';
import SideBar from './Sidebar';

import FeedbackModal from '../FeedbackModal';
import SuccessModal from '../FeedbackModal/SuccessModal';

import NotificationSent from '../MFA/biometric/NotificationSent';
import NeedHelpModal from '../MFA/biometric/NeedHelpModal';
import EnableTwoFAModal from '../MFA/Totp/EnableTwoFAModal';
import QrCodeModal from '../MFA/Totp/QrCodeModal';
import AuthCodeModal from '../MFA/Totp/AuthCodeModal';
import MFAEnabledModal from '../MFA/Totp/MFAEnabledModal';
import PromptModal from '../MFA/Totp/PromptModal';
import LogoutModal from '../MFA/LogoutModal';
import DownloadReport from '../DownloadReport';
import GeneralFiltersV2 from '../Filters/GeneralFilters/GeneralFilters';
import { useWidgetsModalContext } from '../../../shared/providers/WidgetsModalProvider';
import NpsSurvey from '../NPS';
import { AIChatProvider } from '../AIChatSidebar/AIChatContext';
import AIChatPanel from '../AIChatSidebar/AIChatPanel';
import { useMfa } from '../../../utilities/hooks/useMfa';
import { useMfaModalContext } from '../../../shared/providers/MfaModalProvider';
import { useDeviceType } from '../../../utilities/common/deviceType';
import useAuth from '../../../utilities/hooks/useAuth';
import { getAuthToken } from '../../../utilities/common/token';
import useTracking from '../../../utilities/hooks/useTracking';
import { MfaModalState } from '../../../shared/constants/MFA';
import { EventActions, EventNames } from '../../../shared/constants/analytics';
import TrackInstEvents from '../../../utilities/common/TrackInstEvents';
import { useFilterData } from '../../../utilities/hooks/useFilterData';
import { captureUserForMonitoring } from '../../../utilities/common/monitoring';

import './index.css';
import WidgetsLayoutRenderer from '../widgets/components/WidgetsLayoutRenderer';
import WidgetLayoutLoadingBoundary from '../widgets/components/WidgetLayoutLoadingBoundary';
import {
  getWidgetsLayoutContentStyle,
  isDashboardLayoutExcludedPath,
} from '../widgets/utils';
interface LayoutProps {
  children?: ReactNode;
}

function Layout({ children }: LayoutProps) {
  const { trackEvent, user: UserAnalytics } = useTracking();

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [supportMessage, setSupportMessage] = useState<boolean>(false);
  const [nonMfaUser, setNonMfaUser] = useState(false);
  // @ts-ignore
  const pullIntervalRef = useRef<null | NodeJS.Timeout>(null);

  const toggleSidebar = () => {
    trackEvent(EventNames.WellBeingDashboard, {
      eventLabel: 'side_bar_open',
      eventAction: EventActions.click,
      eventCategory: 'side_bar_open',
      eventSource: 'side_bar',
    });

    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleSidebarCollapsed = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Keyboard shortcut for toggling sidebar (Ctrl/Cmd + B)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        toggleSidebarCollapsed();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSidebarCollapsed]);

  const isMobile = useDeviceType();

  useLayoutEffect(() => {
    isMobile && toggleSidebar();
  }, [isMobile]);

  const { slug } = useParams();
  const {
    widgetIds,
    setLayoutData,
    setLayoutLoading,
    feedbackPopupOpen,
    feedbackSuccessOpen,
    setIsLayoutReady,
    setWidgetApiCalled,
    setWidgetData,
    setWidgetLoadingStates,
  } = useWidgetsModalContext();

  const {
    token,
    activeModal,
    setToken,
    needHelpModal,
    promptAlertModal,
    logoutModal,
    isMfaLoading,
    verifyCodeArgs,
    setAllowAccess,
    allowAccess,
  } = useMfaModalContext();

  const { user, refreshUser, selfData } = useAuth();
  const { refetchConfig, configData } = useFilterData();

  const navigate = useNavigate();

  const { refetchMfaCheck, isAccessTokenValid } = useMfa();

  const isExcludedPath = isDashboardLayoutExcludedPath(location.pathname);

  const contentStyle = getWidgetsLayoutContentStyle({
    isMobile,
    isSidebarCollapsed,
  });

  useEffect(() => {
    setTimeout(() => {
      if (supportMessage) {
        setSupportMessage(false);
      }
    }, 3000);
  }, [supportMessage]);
  useEffect(() => {
    setAllowAccess((prevState) => ({
      ...prevState,
      layout: true,
    }));

    if (!user && !getAuthToken()) {
      trackEvent(EventNames.LoginScreen, {
        eventAction: EventActions.click,
        eventCategory: EventNames.LoginScreen,
        eventLabel: 'redirect',
      });
      navigate('/login');
    } else {
      setToken(user?.token);
      refetchConfig();
      refreshUser();
    }
  }, []);

  useEffect(() => {
    if (location.pathname == '/') navigate('/d/dimensions');
    if (selfData) {
      captureUserForMonitoring(selfData?.data.id);
      if (selfData?.data?.mfaEnabled && !isAccessTokenValid()) {
        refetchMfaCheck();
      } else setNonMfaUser(true);
    }
  }, [selfData]);

  const isMfaPassed = isAccessTokenValid() || nonMfaUser;

  useEffect(() => {
    //TODO: Remove this condition during legacy dimensions code clean-up
    // Redirect to the new dimensions dashboard if user tries to access dimensions legacy via URL / browser bookmark
    if (slug === 'dimensions-legacy') {
      navigate('/d/dimensions');
    }

    setLayoutLoading(true);
    // Reset so widgets don't fetch with stale date filters from the previous slug
    setIsLayoutReady(false);
    setLayoutData({});

    setWidgetLoadingStates({});
    setWidgetApiCalled({});
    setWidgetData({});

    if (isMfaPassed) {
      if (!isExcludedPath && location.pathname !== '/') {
        if (slug) {
          // TODO: to check proper handling
          // window.location.reload();
        }
      } else refreshUser();
    }
  }, [slug, isMfaPassed]);

  TrackInstEvents();

  return (
    <div className="intl-layout">
      {!allowAccess.login && (
        <>
          {activeModal === MfaModalState.NotificationSent && (
            <NotificationSent
              enabled={activeModal === MfaModalState.NotificationSent}
            />
          )}

          <NeedHelpModal enabled={needHelpModal} userData={user} />

          <EnableTwoFAModal enabled={activeModal === MfaModalState.Setup2Fa} />

          {activeModal === MfaModalState.ScanQrCode && (
            <QrCodeModal
              enabled={activeModal === MfaModalState.ScanQrCode}
              data={verifyCodeArgs}
            />
          )}

          {activeModal === MfaModalState.Enter2FaCode && (
            <AuthCodeModal
              data={verifyCodeArgs}
              enabled={activeModal === MfaModalState.Enter2FaCode}
            />
          )}

          <MFAEnabledModal enabled={activeModal === MfaModalState.Enabled2Fa} />
          {logoutModal && <LogoutModal open={logoutModal} />}
          <PromptModal enabled={promptAlertModal} userData={user} />
        </>
      )}
      {!isMfaLoading && allowAccess.layout && (
        <>
          {(isAccessTokenValid() || nonMfaUser) && (
            <AIChatProvider>
              <div className="z-40 flex h-auto">
                <div className="fixed top-0 right-0 left-0 z-50 flex">
                  <SideBar
                    toggleSidebar={toggleSidebar}
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    isSidebarCollapsed={isSidebarCollapsed}
                    setIsSidebarCollapsed={setIsSidebarCollapsed}
                    toggleSidebarCollapsed={toggleSidebarCollapsed}
                  />

                  <div className="flex-1">
                    <Header
                      toggleSidebar={toggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      isSidebarCollapsed={isSidebarCollapsed}
                      toggleSidebarCollapsed={toggleSidebarCollapsed}
                      widgetIds={widgetIds}
                    />
                  </div>
                </div>

                {slug ? (
                  <WidgetLayoutLoadingBoundary key={slug}>
                    <WidgetsLayoutRenderer
                      isSidebarCollapsed={isSidebarCollapsed}
                    />
                  </WidgetLayoutLoadingBoundary>
                ) : (
                  <div
                    className={`hrdb-body-container content-transition relative flex flex-1 flex-col overflow-y-auto bg-taupe-50 lg:overflow-y-visible`}
                    style={contentStyle}
                  >
                    <Outlet />
                  </div>
                )}

                {/* Sidebar overlay for mobile */}
                {isSidebarOpen && (
                  <div
                    onClick={toggleSidebar}
                    className="bg-black-900/70 fixed inset-0 z-12 flex justify-end backdrop-blur-xs md:hidden"
                  >
                    <div className="grow" />
                  </div>
                )}
              </div>
              <DownloadReport />
              <GeneralFiltersV2 />
              <FeedbackModal open={feedbackPopupOpen} />
              <SuccessModal open={feedbackSuccessOpen} />
              <NpsSurvey cooldownPeriodElapsed={configData?.showFeedbackForm} />
              <AIChatPanel />
            </AIChatProvider>
          )}
        </>
      )}

      {/* not removing the code just in case needed later */}
      {/* {showLoader && (
        <div className="absolute m-auto flex h-full w-full items-center justify-center py-4 md:px-12">
          <img
            src={loaderDark}
            alt="Loading"
            className="m-auto w-[42px] rounded-full"
          />
        </div>
      )} */}
    </div>
  );
}

export default memo(Layout);
